import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayrollBreakdownTable = ({ employees }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const sortedEmployees = [...employees]?.sort((a, b) => {
    if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
      return sortConfig?.direction === 'asc' ? -1 : 1;
    }
    if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
      return sortConfig?.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedEmployees?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmployees = sortedEmployees?.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return { icon: 'CheckCircle2', color: 'var(--color-success)' };
      case 'pending':
        return { icon: 'Clock', color: 'var(--color-warning)' };
      case 'error':
        return { icon: 'AlertTriangle', color: 'var(--color-error)' };
      default:
        return { icon: 'Circle', color: 'var(--color-muted-foreground)' };
    }
  };

  const formatCurrency = (amount) => {
    return `K${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
            Employee Payroll Breakdown
          </h3>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground hover:text-primary transition-smooth"
                >
                  Employee
                  <Icon name="ArrowUpDown" size={14} color="currentColor" />
                </button>
              </th>
              <th className="px-4 py-3 text-left hidden md:table-cell">
                <button
                  onClick={() => handleSort('employeeId')}
                  className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground hover:text-primary transition-smooth"
                >
                  ID
                  <Icon name="ArrowUpDown" size={14} color="currentColor" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('grossPay')}
                  className="flex items-center justify-end gap-2 font-heading font-semibold text-sm text-foreground hover:text-primary transition-smooth ml-auto"
                >
                  Gross Pay
                  <Icon name="ArrowUpDown" size={14} color="currentColor" />
                </button>
              </th>
              <th className="px-4 py-3 text-right hidden lg:table-cell">
                <span className="font-heading font-semibold text-sm text-foreground">NASFUND</span>
              </th>
              <th className="px-4 py-3 text-right hidden lg:table-cell">
                <span className="font-heading font-semibold text-sm text-foreground">SWT</span>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="font-heading font-semibold text-sm text-foreground">Status</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedEmployees?.map((employee) => {
              const nasfundStatus = getStatusIcon(employee?.nasfundStatus);
              const swtStatus = getStatusIcon(employee?.swtStatus);
              
              return (
                <tr key={employee?.id} className="hover:bg-muted/50 transition-smooth">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-body font-medium text-sm text-foreground">{employee?.name}</p>
                      <p className="text-xs caption text-muted-foreground">{employee?.department}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="data-text text-sm text-muted-foreground">{employee?.employeeId}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="data-text font-medium text-sm text-foreground">
                      {formatCurrency(employee?.grossPay)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell">
                    <div className="flex items-center justify-end gap-2">
                      <span className="data-text text-sm text-foreground">
                        {formatCurrency(employee?.nasfund)}
                      </span>
                      <Icon name={nasfundStatus?.icon} size={16} color={nasfundStatus?.color} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell">
                    <div className="flex items-center justify-end gap-2">
                      <span className="data-text text-sm text-foreground">
                        {formatCurrency(employee?.swt)}
                      </span>
                      <Icon name={swtStatus?.icon} size={16} color={swtStatus?.color} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <Icon name={nasfundStatus?.icon} size={16} color={nasfundStatus?.color} />
                      <Icon name={swtStatus?.icon} size={16} color={swtStatus?.color} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border flex items-center justify-between">
        <p className="text-sm caption text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedEmployees?.length)} of {sortedEmployees?.length}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            iconName="ChevronLeft"
          >
            Previous
          </Button>
          <span className="text-sm data-text text-foreground px-3">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            iconName="ChevronRight"
            iconPosition="right"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PayrollBreakdownTable;